import React, {useState, useEffect } from 'react';
import Table from 'react-bootstrap/Table';
import OrderLine from './OrderLine.jsx';
import { formatMoney } from './utils.js';
import { Form, FormCheck, InputGroup } from 'react-bootstrap';


// TODO - Make this configurable
const siteDepositPercentage = __DEPOSIT_PERCENTAGE__ ? parseInt(__DEPOSIT_PERCENTAGE__) / 100 : 0.3;

export default function OrderTable({ products,
                                        setOrder,
                                        selectedUpsells,
                                        setSelectedUpsells,
                                        deposit,
                                        setDeposit,
                                        invalidReasons,
                                        setInvalidReasons,
                                        isSkinny,
                                        setIsSkinny,
                                        note,
                                        setNote
                                    }) {

    const [subtotal, setSubtotal] = useState(0);
    const [discountTotal, setDiscountTotal] = useState(0);

    const orderHasUmbrella = products.some(product => {
        return product?.name.includes('Umbrella');
    });
    const depositPercentage = orderHasUmbrella ? 1 : siteDepositPercentage;

    // Calculate subtotal
    useEffect(() => {
        const productsubtotal = products.reduce((sum, product) => sum + (parseFloat(product.price) || 0) * (parseInt(product.qty) || 1), 0);

        let totalDiscount = products.reduce((sum, product) => sum + (product.discount || 0), 0);
        const selectedUpsellSubtotal = Object.values(selectedUpsells).reduce((sum, upsells) => {
            return sum + Object.entries(upsells).reduce((upsellSum, [sku, {qty, discount, price}]) => {
                if (qty <= 0) {
                    return upsellSum;
                }

                totalDiscount += discount; // Intentional side-effect
                return upsellSum + (price * qty);
            }, 0);
        }, 0);

        const subtotal = productsubtotal + selectedUpsellSubtotal;
        setSubtotal(subtotal);
        setDiscountTotal(totalDiscount);

    }, [products, selectedUpsells]);

    // Check if deposit is valid
    const requiredDeposit = (subtotal - discountTotal) * depositPercentage;
    useEffect(() => {
        const depositIsSufficient = (deposit || 0) >= requiredDeposit;

        // Reset skinny flag if deposit is sufficient
        if ((!deposit || depositIsSufficient) && isSkinny) {
            setIsSkinny(false);

        } else if (deposit && !depositIsSufficient && !isSkinny) {
            setIsSkinny(true);
        }

    }, [deposit, requiredDeposit, isSkinny]);

    const fullPaymentRequired = depositPercentage === 1;
    return (
        <Table bordered>
            <thead>
                <tr>
                    <th>Serial Number</th>
                    <th>SKU</th>
                    <th>Name</th>
                    <th>QTY</th>
                    <th>Price</th>
                    <th>Discount</th>
                    <th style={{minWidth: 150}}>Total</th>
                </tr>
            </thead>
            <tbody>
                {products.map((product, idx) => {
                    const upsells = selectedUpsells?.[product.sku] || [];
                    const updateUpsells = (newUpsells) => {
                        selectedUpsells[product.sku] = newUpsells;
                        setSelectedUpsells({ ...selectedUpsells });
                    };

                    return <OrderLine
                        key={`orderline-${idx}`}
                        product={product}
                        idx={idx}
                        order={products}
                        setOrder={setOrder}
                        selectedUpsells={upsells}
                        setSelectedUpsells={updateUpsells}
                        invalidReasons={invalidReasons}
                        setInvalidReasons={setInvalidReasons}
                    />
                })}
            </tbody>
            <tfoot>

                <tr>
                    <td rowSpan="5" colSpan="3" className="border-0" style={{ verticalAlign: 'bottom' }}>
                        <Form.Label>Note:</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={4}
                            placeholder="Optional note to appear on the order"
                            value={note || ''}
                            onChange={e => setNote(e.target.value)}
                        />
                    </td>
                    <td colSpan="3" className="border-0"  style={{ textAlign: 'right' }}>
                        Order subtotal (inc. GST)
                    </td>
                    <td style={{ textAlign: 'right' }}>
                        {formatMoney(subtotal)}
                    </td>
                </tr>

                <tr>
                    <td colSpan="3" className="border-0"  style={{ textAlign: 'right' }}>
                        Discount
                    </td>
                    <td style={{ textAlign: 'right' }}>
                        {formatMoney(discountTotal)}
                    </td>
                </tr>

                <tr>
                    <td colSpan="3" className="border-0"  style={{ textAlign: 'right' }}>
                        Total (inc. GST)
                    </td>
                    <td style={{ textAlign: 'right' }}>
                        {formatMoney(subtotal - discountTotal)}
                    </td>
                </tr>

                <tr>
                    <td colSpan="3" className="border-0"  style={{ textAlign: 'right' }}>
                        { fullPaymentRequired ? `Full Payment Required` : `Required ${depositPercentage * 100}% Deposit`}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                        {formatMoney((subtotal - discountTotal) * depositPercentage)}
                    </td>
                </tr>
                <tr>
                    <th colSpan="3" className="text-right border-0">
                        { fullPaymentRequired ? `Payment Received` : `Deposit Paid Today` }<br />
                        <small style={{fontWeight: 'normal'}}><a href="#"onClick={e => {
                            e.preventDefault();
                            const requiredDeposit = (subtotal - discountTotal) * depositPercentage;
                            setDeposit(requiredDeposit);
                        }}>Set to { fullPaymentRequired ? 'full payment' : 'required deposit' }</a></small>
                    </th>
                    <th className="text-right">
                        <InputGroup>
                            <InputGroup.Prepend>
                                <InputGroup.Text>$</InputGroup.Text>
                            </InputGroup.Prepend>
                            <Form.Control
                                value={deposit || ''}
                                style={{ minWidth: 80, textAlign: 'right', outline: invalidReasons.includes('deposit') ? '2px solid red' : '' }}
                                type="number"
                                onChange={e => {
                                    setDeposit(parseFloat(e.target.value) || 0);
                                }}
                            />
                        </InputGroup>
                        { requiredDeposit > deposit && !orderHasUmbrella && <>
                                <FormCheck
                                    type="checkbox"
                                    id="skinny-deposit"
                                    label="Skinny Deposit"
                                    checked={isSkinny||false}
                                    onChange={(e) => setIsSkinny(e.target.checked)}
                                    style={{ fontSize: '0.8rem', textAlign: 'left' }}
                                    />

                                { isSkinny && <p><small className="badge badge-warning">Expires {new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toLocaleDateString('en-GB')}</small></p>}
                            </>
                        }
                    </th>
                </tr>
            </tfoot>
        </Table>
    );
}
